/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as AuthActions from '@auth/store/auth.actions';
import { isAuthenticated } from '@auth/store/auth.selectors';
import { State } from '@core/store';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Vehicle } from '@shared/models';
import { VehiclesService } from '@vehicles/services/vehicles.service';
import * as VehicleActions from '@vehicles/store/vehicles.actions';
import { getCurrentVehicle } from '@vehicles/store/vehicles.selectors';
import { VehicleDetailComponent } from '@vehicles/vehicle-detail/vehicle-detail.component';
import { merge, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  VehicleDeactivationConfirmationComponent
} from '@vehicles/vehicle-deactivation-confirmation/vehicle-deactivation-confirmation.component';

@Injectable()
export class VehiclesEffects {
  constructor(
    private actions$: Actions<VehicleActions.VehicleActions>,
    private vehiclesService: VehiclesService,
    private dialogService: MatDialog,
    private snackBarService: MatSnackBar,
    private translateService: TranslateService,
    private store: Store<State>
  ) { }

  
  init$ = createEffect(() => this.actions$.pipe(
    ofType(ROOT_EFFECTS_INIT),
    withLatestFrom(this.store.select(isAuthenticated)),
    map(authenticated => {
      let licenseClasses = [];
      if (authenticated) {
        licenseClasses = JSON.parse(localStorage.getItem('licenseClasses'));
      }
      return new VehicleActions.Init(licenseClasses);
    })
  ));

  
  loadLicenceClasses$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.LOGIN_SUCCESS),
    switchMap(() => this.vehiclesService.getLicenseClasses().pipe(
      tap(licenseClasses => localStorage.setItem('licenseClasses', JSON.stringify(licenseClasses))),
      switchMap(licenceClasses => of(new VehicleActions.LoadLicenseClassesSuccess(licenceClasses))),
      catchError(() => of(new VehicleActions.LoadLicenseClassesFailure()))
    ))
  ));

  
  loadVehicles$ = createEffect(() => this.actions$.pipe(
    ofType(VehicleActions.LOAD_VEHICLES),
    switchMap(() => this.vehiclesService.getVehicles().pipe(
      map(vehicles => new VehicleActions.LoadVehiclesSuccess(vehicles)),
      catchError(() => of(new VehicleActions.LoadVehiclesFailure()))
    ))
  ));

  
  loadVehicle$ = createEffect(() => this.actions$.pipe(
    ofType(VehicleActions.LOAD_VEHICLE),
    map(action => action.vehicleId),
    tap(() => this.dialogService.open(VehicleDetailComponent, {
      width: '25rem'
    })),
    switchMap(vehicleId => {
      const getVehicle$ = of(vehicleId);
      const createVehicle$ = getVehicle$.pipe(
        filter(id => !id),
        switchMap(() => of({ } as Vehicle))
      );
      const loadVehicle$ = getVehicle$.pipe(
        filter(id => !!id),
        switchMap(id => this.vehiclesService.getVehicle(id))
      );

      return merge(createVehicle$, loadVehicle$).pipe(
        map(vehicle => new VehicleActions.LoadVehicleSuccess(vehicle)),
        catchError(() => of(new VehicleActions.LoadVehicleFailure()))
      );
    })
  ));

  
  loadVehicleFailure$ = createEffect(() => this.actions$.pipe(
    ofType(VehicleActions.LOAD_VEHICLE_FAILURE),
    switchMap(() => this.translateService.get('vehicles.loadVehiclesError').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  persistVehicle$ = createEffect(() => this.actions$.pipe(
    ofType(VehicleActions.PERSIST_VEHICLE),
    map(action => action.vehicle),
    withLatestFrom(this.store.select(getCurrentVehicle)),
    switchMap(([vehicle, currentVehicle]) => {
      const existingId = currentVehicle['@id'];
      const persistVehicle$ = existingId
        ? this.vehiclesService.updateVehicle({...vehicle, '@id': existingId})
        : this.vehiclesService.createVehicle(vehicle);

      return persistVehicle$.pipe(
        map(() => new VehicleActions.PersistVehicleSuccess()),
        catchError(() => of(new VehicleActions.PersistVehicleFailure()))
      );
    })
  ));

  
  persistVehicleFailure$ = createEffect(() => this.actions$.pipe(
    ofType(VehicleActions.PERSIST_VEHICLE_FAILURE),
    switchMap(() => this.translateService.get('vehicles.persistVehicleError').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  persistVehicleSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(VehicleActions.PERSIST_VEHICLE_SUCCESS),
    tap(() => this.dialogService.closeAll()),
    map(() => new VehicleActions.LoadVehicles())
  ));

  
  deactivateDriver$ = createEffect(() => this.actions$.pipe(
    ofType(VehicleActions.DEACTIVATE_VEHICLE),
    map((action: VehicleActions.DeactivateVehicle) => action.vehicle),
    switchMap(vehicle => this.dialogService.open(VehicleDeactivationConfirmationComponent, {
        data: {vehicle}
      }).afterClosed().pipe(
        map(confirmation => [confirmation, vehicle])
      )
    ),
    filter(confirmation => confirmation[0] === true),
    map(confirmation => confirmation[1] as Vehicle),
    switchMap(vehicle => this.vehiclesService.deactivateVehicle(vehicle)),
    tap(_ => this.store.dispatch(new VehicleActions.LoadVehicles()))
  ), {dispatch: false});
}
